import { useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import emailjs from "@emailjs/browser";
import { useTranslation } from "react-i18next";
import SiteLayout from "@/components/feature/SiteLayout";
import PageBanner from "@/components/feature/PageBanner";
import { useSiteInfo } from "@/hooks/useSiteInfo";

interface QuotationStep {
  step: number;
  title: string;
  desc: string;
}

export default function QuotationPage() {
  const { t } = useTranslation();
  const site = useSiteInfo();
  const productOptionsRaw = t("quotation.productOptions", { returnObjects: true }) as unknown as string[];
  const requiredInfoRaw = t("quotation.page.requiredInfo", { returnObjects: true }) as unknown as string[];
  const processStepsRaw = t("quotation.page.process", { returnObjects: true }) as unknown as QuotationStep[];
  const productOptions = Array.isArray(productOptionsRaw) ? productOptionsRaw : [];
  const requiredInfo = Array.isArray(requiredInfoRaw) ? requiredInfoRaw : [];
  const processSteps = Array.isArray(processStepsRaw) ? processStepsRaw : [];

  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const honeypot = form.querySelector<HTMLInputElement>('[name="company_alt"]');
    if (honeypot && honeypot.value.trim() !== "") {
      setSubmitted(true);
      return;
    }

    setSubmitting(true);
    const formData = new FormData(form);
    if (honeypot) formData.delete("company_alt");

    try {
      await fetch("https://readdy.ai/api/form/d8tkigcal24muhn2r8a0", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
    } catch {
      // silently handle
    }

    emailjs.sendForm("service_ml5rlis", "template_tyiwc9t", form).catch(() => {});

    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <SiteLayout navVariant="solid">
        <PageBanner
          title={t("quotation.page.title")}
          subtitle={t("quotation.page.submittedSubtitle")}
          breadcrumb={[
            { label: t("support.page.title"), to: "/support" },
            { label: t("quotation.page.title") },
          ]}
          bgImage="https://readdy.ai/api/search-image?query=Modern%20clean%20industrial%20office%20interior%20with%20professional%20atmosphere%2C%20engineering%20consultation%20desk%20with%20technical%20documents%2C%20warm%20natural%20lighting%2C%20corporate%20manufacturing%20business%20environment%2C%20clean%20organized%20workspace&width=1600&height=680&seq=krubber-quotation-banner&orientation=landscape"
        />
        <section className="py-20 bg-background-50">
          <div className="max-w-[640px] mx-auto px-6 text-center">
            <span className="w-20 h-20 mx-auto rounded-2xl bg-primary-100 flex items-center justify-center">
              <i className="ri-check-line text-primary-600 text-3xl" />
            </span>
            <h2 className="mt-6 text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
              {t("quotation.page.submittedTitle")}
            </h2>
            <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
              {t("quotation.page.submittedDesc")}{" "}
              <strong className="text-foreground-950">{site.tel}</strong>
            </p>
            <div className="mt-10 flex flex-col sm:flex-row gap-3 justify-center">
              <Link
                to="/products"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full border border-background-300 text-foreground-700 hover:bg-background-100 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-arrow-left-line" />
                {t("quotation.page.viewProducts")}
              </Link>
              <Link
                to="/technical"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap"
              >
                <i className="ri-file-list-3-line" />
                {t("quotation.page.viewTech")}
              </Link>
            </div>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout navVariant="solid">
      <PageBanner
        title={t("quotation.page.title")}
        subtitle={t("quotation.page.bannerSubtitle")}
        breadcrumb={[
          { label: t("support.page.title"), to: "/support" },
          { label: t("quotation.page.title") },
        ]}
        bgImage="https://readdy.ai/api/search-image?query=Modern%20clean%20industrial%20office%20interior%20with%20professional%20atmosphere%2C%20engineering%20consultation%20desk%20with%20technical%20documents%2C%20warm%20natural%20lighting%2C%20corporate%20manufacturing%20business%20environment%2C%20clean%20organized%20workspace&width=1600&height=680&seq=krubber-quotation-banner&orientation=landscape"
      />

      <section className="py-16 md:py-20 bg-background-50">
        <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
          <div className="flex flex-col lg:flex-row gap-12 lg:gap-20">
            {/* Left: Info */}
            <div className="lg:w-[420px] flex-shrink-0">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                {t("quotation.page.guideTitle")}
              </h2>
              <p className="mt-3 text-sm text-foreground-600 leading-relaxed">
                {t("quotation.page.guideDesc")}
              </p>

              <div className="mt-8 p-6 rounded-xl bg-background-100 border border-background-200/70">
                <p className="text-sm font-bold text-foreground-800 mb-4">{t("quotation.page.requiredInfoTitle")}</p>
                <ul className="flex flex-col gap-3">
                  {requiredInfo.map((info) => (
                    <li key={info} className="flex items-start gap-2.5 text-sm text-foreground-700">
                      <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <i className="ri-checkbox-circle-line text-primary-500" />
                      </span>
                      {info}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-6 p-6 rounded-xl bg-accent-50 border border-accent-200/60">
                <p className="text-sm font-bold text-foreground-800 mb-3">{t("quotation.page.processTitle")}</p>
                <div className="flex flex-col gap-4">
                  {processSteps.map((item) => (
                    <div key={item.step} className="flex items-start gap-3">
                      <span className="w-8 h-8 rounded-full bg-accent-100 text-accent-700 flex items-center justify-center text-xs font-extrabold flex-shrink-0">
                        {item.step}
                      </span>
                      <div>
                        <p className="text-sm font-semibold text-foreground-900">{item.title}</p>
                        <p className="text-xs text-foreground-500 mt-0.5">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-6 flex flex-col gap-3">
                <a
                  href={site.telLink}
                  className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-colors cursor-pointer"
                >
                  <span className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-phone-line text-primary-600" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{t("quotation.page.telInquiry")}</p>
                    <p className="text-sm font-bold text-foreground-950">{site.tel}</p>
                  </div>
                </a>
                <a
                  href={site.emailLink}
                  className="flex items-center gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-200/60 transition-colors cursor-pointer"
                >
                  <span className="w-10 h-10 rounded-xl bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600" />
                  </span>
                  <div>
                    <p className="text-xs text-foreground-500">{t("quotation.page.emailInquiry")}</p>
                    <p className="text-sm font-bold text-foreground-950">{site.email}</p>
                  </div>
                </a>
              </div>
            </div>

            {/* Right: Form */}
            <div className="flex-1 min-w-0">
              <h2 className="text-2xl md:text-3xl font-heading font-extrabold text-foreground-950">
                {t("quotation.page.onlineTitle")}
              </h2>
              <p className="mt-3 text-sm text-foreground-600">
                {t("quotation.page.onlineDesc")}
              </p>

              <form data-readdy-form onSubmit={handleSubmit} className="mt-8 flex flex-col gap-5">
                {/* Row 1: Company + Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="company" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("quotation.page.form.company")} <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="company"
                      name="company"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("quotation.page.form.companyPlaceholder")}
                    />
                  </div>
                  <div>
                    <label htmlFor="name" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("quotation.page.form.name")} <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("quotation.page.form.namePlaceholder")}
                    />
                  </div>
                </div>

                {/* Row 2: Phone + Email */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("quotation.page.form.phone")} <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("quotation.page.form.phonePlaceholder")}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("quotation.page.form.email")} <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("quotation.page.form.emailPlaceholder")}
                    />
                  </div>
                </div>

                {/* Product + Name */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="product_category" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("quotation.page.form.productCategory")} <span className="text-accent-500">*</span>
                    </label>
                    <select
                      id="product_category"
                      name="product_category"
                      required
                      defaultValue=""
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors cursor-pointer"
                    >
                      <option value="">{t("quotation.page.form.productCategoryPlaceholder")}</option>
                      {productOptions.map((opt) => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label htmlFor="product_name" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("quotation.page.form.productName")}
                    </label>
                    <input
                      type="text"
                      id="product_name"
                      name="product_name"
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("quotation.page.form.productNamePlaceholder")}
                    />
                  </div>
                </div>

                {/* Quantity + Deadline */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                  <div>
                    <label htmlFor="quantity" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("quotation.page.form.quantity")} <span className="text-accent-500">*</span>
                    </label>
                    <input
                      type="text"
                      id="quantity"
                      name="quantity"
                      required
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("quotation.page.form.quantityPlaceholder")}
                    />
                  </div>
                  <div>
                    <label htmlFor="deadline" className="block text-xs font-bold text-foreground-600 mb-2">
                      {t("quotation.page.form.deadline")}
                    </label>
                    <input
                      type="text"
                      id="deadline"
                      name="deadline"
                      className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors"
                      placeholder={t("quotation.page.form.deadlinePlaceholder")}
                    />
                  </div>
                </div>

                {/* Requirements */}
                <div>
                  <label htmlFor="requirements" className="block text-xs font-bold text-foreground-600 mb-2">
                    {t("quotation.page.form.requirements")} <span className="text-accent-500">*</span>
                  </label>
                  <textarea
                    id="requirements"
                    name="requirements"
                    rows={5}
                    required
                    maxLength={500}
                    className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200/70 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300 focus:ring-2 focus:ring-primary-100 transition-colors resize-none"
                    placeholder={t("quotation.page.form.requirementsPlaceholder")}
                  />
                  <p className="mt-1 text-xs text-foreground-400">{t("quotation.page.form.maxLengthHint")}</p>
                </div>

                {/* File Upload */}
                <div>
                  <label htmlFor="drawing_file" className="block text-xs font-bold text-foreground-600 mb-2">
                    {t("quotation.page.form.drawingFile")}
                  </label>
                  <input
                    type="file"
                    id="drawing_file"
                    name="drawing_file"
                    className="w-full text-sm text-foreground-600 file:mr-4 file:py-2.5 file:px-5 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-primary-100 file:text-primary-700 hover:file:bg-primary-200 file:cursor-pointer"
                  />
                  <p className="mt-1 text-xs text-foreground-400">
                    {t("quotation.page.form.drawingFileHint")}
                  </p>
                </div>

                {/* Privacy consent */}
                <div className="flex items-start gap-3 mt-2">
                  <input
                    type="checkbox"
                    id="privacy_consent"
                    name="privacy_consent"
                    required
                    className="mt-1 w-4 h-4 rounded border-background-300 text-primary-500 focus:ring-primary-400 cursor-pointer"
                  />
                  <label htmlFor="privacy_consent" className="text-xs text-foreground-600 leading-relaxed">
                    {t("quotation.page.form.consent")} <span className="text-accent-500">*</span>
                  </label>
                </div>

                {/* Honeypot */}
                <input
                  type="text"
                  name="company_alt"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="hp-shield"
                />

                {/* Submit */}
                <button
                  type="submit"
                  disabled={submitting}
                  className="mt-4 inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {submitting ? (
                    <>
                      <span className="w-4 h-4 border-2 border-background-50/30 border-t-background-50 rounded-full animate-spin" />
                      {t("quotation.page.form.submitting")}
                    </>
                  ) : (
                    <>
                      <i className="ri-mail-send-line" />
                      {t("quotation.page.form.submit")}
                    </>
                  )}
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </SiteLayout>
  );
}